import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Clock, Users, Star, ArrowRight } from 'lucide-react';
import { LearningTrack } from '@/types/learningPathTypes';

interface LearningPathCardProps {
  track: LearningTrack;
  progress?: number;
  isEnrolled?: boolean;
  onEnroll?: () => void;
  onContinue?: () => void;
}

export const LearningPathCard: React.FC<LearningPathCardProps> = ({
  track,
  progress = 0,
  isEnrolled = false,
  onEnroll,
  onContinue
}) => {
  const completedPathways = Math.floor((progress / 100) * track.pathways.length);
  const totalPathways = track.pathways.length;

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border-muted/20 hover:border-primary/20">
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${track.color}15`, color: track.color }}
            >
              {track.icon}
            </div>
            <div>
              <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                {track.name}
              </CardTitle>
              <Badge variant="secondary" className="mt-1 text-xs">
                {track.type.charAt(0).toUpperCase() + track.type.slice(1)}
              </Badge>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs">
            {track.pathways.length} Pathways
          </Badge>
        </div>
        
        <p className="text-muted-foreground text-sm leading-relaxed">
          {track.description}
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Progress Section */}
        {isEnrolled && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Progress</span>
              <span className="text-sm font-semibold text-foreground">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {completedPathways} of {totalPathways} pathways completed
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span className="text-sm">{track.estimatedDuration}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span className="text-sm">{totalPathways} pathways</span>
          </div>
        </div>

        {/* Levels */}
        <div className="space-y-2">
          <span className="text-sm font-medium text-muted-foreground">Learning Levels</span>
          <div className="flex flex-wrap gap-1">
            {track.levels.map((level) => (
              <Badge key={level} variant="outline" className="text-xs capitalize">
                {level}
              </Badge>
            ))}
          </div>
        </div>

        {/* Key Skills Preview */}
        <div className="space-y-2">
          <span className="text-sm font-medium text-muted-foreground">Key Skills</span>
          <div className="flex flex-wrap gap-1">
            {track.pathways.slice(0, 2).map((pathway) => 
              pathway.skills.slice(0, 3).map((skill) => (
                <Badge key={skill} variant="secondary" className="text-xs">
                  {skill}
                </Badge>
              ))
            )}
            {track.pathways.length > 2 && (
              <Badge variant="secondary" className="text-xs">
                +{track.pathways.slice(2).reduce((acc, p) => acc + p.skills.length, 0)} more
              </Badge>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-2">
          {isEnrolled ? (
            <Button 
              onClick={onContinue}
              className="w-full group/btn"
              size="lg"
            >
              Continue Learning
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          ) : (
            <Button 
              onClick={onEnroll}
              variant="outline"
              className="w-full group/btn hover:bg-primary hover:text-primary-foreground"
              size="lg"
            >
              Start Learning
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};